import PropTypes from 'prop-types';
import {useRef} from 'react';
import {useTheme} from 'react-jss';
import {connect} from 'react-redux';
import ScreenReaderOnly from '../../UI/ScreenReaderOnly/ScreenReaderOnly';
import EmptyOption from '../components/address/EmptyOption';
import Translation from '../lang/Translation';
import isUsingArtemisStyles from '../selectors/isUsingArtemisStyles';
import useStyles from './ExpiryDateField.styles';
import selectUseStyles from './SelectField.styles';
import Field2 from './Field2';
import FieldWrapper from './FieldWrapper/FieldWrapper';

function ExpiryDateField(props) {
  const hasBlurred = useRef({
    'paymentCard.expirationMonth': false,
    'paymentCard.expirationYear': false,
  });
  
  // Convert the events fired by the fields to a generic event for the component, as though it is just one field
  const getFakeEvent = (event, existingValues) => {
    const fakeEvent = {
      target: {
        name: 'paymentCard.expiryDate',
        value: existingValues || {},
      },
    };
    
    if (event.target.name === 'paymentCard.expirationMonth') {
      fakeEvent.target.value.expirationMonth = event.target.value;
    } else { // "paymentCard.expirationYear"
      fakeEvent.target.value.expirationYear = event.target.value;
    }
    
    return fakeEvent;
  };
  
  const renderExpiryMonths = () => {
    const options = [
      <EmptyOption
        key="empty"
        translationKey="checkout.payment.method.field.expires.month.instruction.option.text"
      />,
    ];
    
    for (let month = 1; month <= 12; month++) {
      const monthString = month < 10 ? `0${month}` : `${month}`;
      options.push(<option value={monthString} key={monthString}>{monthString}</option>);
    }
    
    return options;
  };
  
  const renderExpiryYears = () => {
    const options = [
      <EmptyOption key="empty" translationKey="checkout.payment.method.field.expires.year.instruction.option.text" />,
    ];
    
    // Render this and next 20 years
    const maxYear = new Date().getFullYear() + 20;
    for (let year = new Date().getFullYear(); year <= maxYear; year++) {
      options.push(<option value={year} key={year}>{year.toString().substr(-2)}</option>);
    }
    
    return options;
  };
  
  const getChangeHandler = onChange => (
    (event) => {
      onChange(getFakeEvent(event, props.value));
    }
  );
  
  const getBlurHandler = onBlur => (
    (event) => {
      hasBlurred.current[event.target.name] = true;
      
      // If both expirationMonth and expirationYear have blurred, then validate.
      // This is just so that the customer isn't annoyed by the 'please enter valid expiry'
      // message before they've even had a chance to enter their card.
      const shouldValidate = (hasBlurred.current['paymentCard.expirationMonth'] && hasBlurred.current['paymentCard.expirationYear']);
      
      onBlur(getFakeEvent(event, props.value), shouldValidate);
    }
  );
  
  const getMonthKeyPressHandler = onChange => (
    (event) => {
      // If the user pressed number X, we want to select 0X instead (e.g. press '4', select '04')
      if (event.key.match(/[1-9]/)) {
        const setMonth = (e) => {
          e.preventDefault();
          e.target.value = `0${e.key}`; // Value to choose
          onChange(getFakeEvent(e, props.value));
        };
        
        // If no month is yet set, always set
        if (!props.value || !props.value.expirationMonth) {
          setMonth(event);
        } else if (event.key.match(/[3-9]/)) { // If 3-9, always go to the option.
          setMonth(event);
        } else if (event.key === '1' && props.value.expirationMonth === '12') { // If 1 and existing selection is 12, go to 01.
          setMonth(event);
        } else if (event.key === '2' && props.value.expirationMonth.indexOf('1') === -1) { // If 2 is pressed an option does not contain a 1, go to 02
          setMonth(event);
        }
      }
    }
  );
  
  const definedValue = {expirationMonth: '', expirationYear: '', ...props.value}; // in case it's not yet set in the state.
  const theme = useTheme();
  const classes = useStyles({...props, theme});
  const selectClasses = selectUseStyles({...props, theme});
  
  return (
    <Field2
      fieldContainer={props.fieldContainer}
      definedField={props.definedField}
      value={definedValue}
      validationCallback={props.validationCallback}
      nameOnForm={props.legacyNames.nameOnForm}
    >
      {(onChange, onBlur, onFocus, errorText) => {
        const handleChange = getChangeHandler(onChange);
        const handleBlur = getBlurHandler(onBlur);
        const handleMonthKeyPress = getMonthKeyPressHandler(onChange);
        
        return (
          <FieldWrapper
            label={props.definedField.label}
            required={props.definedField.required}
            link={props.definedField.link}
            toolTip={props.definedField.toolTip}
            htmlFor="expiration-month"
            wrappedClassName={props.legacyNames.wrappedClassName}
            errorText={errorText}
          >
            <select
              onChange={handleChange}
              onBlur={handleBlur}
              id="expiration-month"
              name="paymentCard.expirationMonth"
              onFocus={onFocus}
              className={`${classes.expirationMonth} ${selectClasses.selectField} expiry-month-field ${props.legacyNames.nameInClass} ${errorText ? 'error-block-v2' : ''}`}
              value={definedValue.expirationMonth}
              autoComplete="cc-exp-month"
              onKeyPress={handleMonthKeyPress}
            >
              {renderExpiryMonths()}
            </select>
  
            <ScreenReaderOnly>
              <label htmlFor="expiration-year">
                <Translation>checkout.payment.method.field.label.expires.year.text</Translation>
              </label>
            </ScreenReaderOnly>
            
            <select
              onChange={handleChange}
              onBlur={handleBlur}
              id="expiration-year"
              name="paymentCard.expirationYear"
              className={`${classes.expirationYear} ${selectClasses.selectField} expire-year-field ${props.legacyNames.nameInClass} ${errorText ? 'error-block-v2' : ''}`}
              value={definedValue.expirationYear}
              autoComplete="cc-exp-year"
            >
              {renderExpiryYears()}
            </select>
          </FieldWrapper>
        );
      }}
    </Field2>
  );
}

const mapStateToProps = state => ({
  isUsingArtemisStyles: isUsingArtemisStyles(state),
});

ExpiryDateField.propTypes = {
  definedField: PropTypes.object.isRequired,
  fieldContainer: PropTypes.object.isRequired,
  fieldNamePrefix: PropTypes.string.isRequired,
  
  // optional
  validationCallback: PropTypes.object,
  value: PropTypes.shape({
    expirationMonth: PropTypes.string,
    expirationYear: PropTypes.string,
  }),
  legacyNames: PropTypes.object,
  children: PropTypes.node,
  isUsingArtemisStyles: PropTypes.bool,
};

export default connect(mapStateToProps)(ExpiryDateField);
